import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { config } from '@/config';
import { getAuthToken, setAuthToken, clearAuthToken } from '@/lib/authTokenStorage';
import { invalidateAdminAccessCache } from '@/lib/adminAccessCache';

const AuthContext = createContext();

const BACKEND_URL = config.BACKEND_URL;
const API = BACKEND_URL;

// Intercepteur axios : envoyer le token sur toutes les requêtes vers l'API
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  if (url.includes('/api/') || url.startsWith('/api') || url.includes(BACKEND_URL)) {
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Parcours 1ère connexion : uniquement après login() explicite, pas au checkAuth sur /login */
  const [onboardingRequested, setOnboardingRequested] = useState(false);

  // Indique si l'utilisateur est actuellement authentifié (pour distinguer un 401 de démarrage d'un 401 en session)
  const isAuthenticatedRef = useRef(false);
  // Évite d'afficher le toast plusieurs fois si plusieurs requêtes échouent en même temps
  const sessionExpiredShownRef = useRef(false);
  // Référence vers l'ID utilisateur courant pour le nettoyage de la session admin
  const userIdRef = useRef(null);

  // Intercepteur axios : détecter l'expiration de session (401) pendant une session active
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 401 &&
          isAuthenticatedRef.current &&
          !sessionExpiredShownRef.current
        ) {
          sessionExpiredShownRef.current = true;
          isAuthenticatedRef.current = false;

          // Nettoyage session admin PIN
          if (userIdRef.current) {
            sessionStorage.removeItem(`admin_unlocked_${userIdRef.current}`);
          }
          clearAuthToken();
          setUser(null);
          setToken(null);
          setOnboardingRequested(false);
          userIdRef.current = null;

          toast.error('Session expirée', {
            description: 'Votre session a expiré. Veuillez vous reconnecter.',
            duration: 8000,
          });
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    let storedToken = getAuthToken();
    if (!storedToken) {
      setLoading(false);
      return;
    }
    // Nouvel onglet : sessionStorage vide → token lu depuis localStorage ; recopier pour le reste du code legacy
    try {
      if (!sessionStorage.getItem('token') && localStorage.getItem('token')) {
        sessionStorage.setItem('token', storedToken);
      }
    } catch {
      /* ignore */
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        params: { token: storedToken }
      });
      setUser(response.data);
      setToken(storedToken);
      setAuthToken(storedToken);
      isAuthenticatedRef.current = true;
      sessionExpiredShownRef.current = false;
      userIdRef.current = response.data?.id || null;
    } catch (error) {
      clearAuthToken();
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      username,
      password
    });
    setAuthToken(response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    setOnboardingRequested(true);
    isAuthenticatedRef.current = true;
    sessionExpiredShownRef.current = false;
    userIdRef.current = response.data.user?.id || null;
    return response.data;
  };

  const clearOnboardingRequest = () => {
    setOnboardingRequested(false);
  };

  const logout = () => {
    // Effacer la session admin de cet utilisateur avant de perdre son ID
    if (user?.id) {
      sessionStorage.removeItem(`admin_unlocked_${user.id}`);
    }
    clearAuthToken();
    invalidateAdminAccessCache();
    setUser(null);
    setToken(null);
    setOnboardingRequested(false);
    isAuthenticatedRef.current = false;
    sessionExpiredShownRef.current = false;
    userIdRef.current = null;
  };

  // Mettre à jour les données utilisateur localement (sans appel API)
  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Rafraîchir les données utilisateur depuis le serveur
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        params: { token }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Erreur rafraîchissement user:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        checkAuth,
        updateUser,
        refreshUser,
        onboardingRequested,
        clearOnboardingRequest,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
